import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc, increment, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { notifyHelpOffered } from '../../utils/sendNotification';

export const RequestFeed = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);

  const subjects = ['All', 'Math', 'English', 'Science', 'History', 'Geography', 'Physics', 'Chemistry', 'Biology', 'Chinese', 'French', 'Economics', 'Business', 'Computer Science', 'Other'];

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    let filtered = requests;
    if (filter !== 'All') {
      filtered = filtered.filter(r => r.subject === filter);
    }
    if (search) {
      filtered = filtered.filter(r => 
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.description.toLowerCase().includes(search.toLowerCase())
      );
    }
    setFilteredRequests(filtered);
  }, [requests, filter, search]);

  const fetchRequests = async () => {
    try {
      const q = query(collection(db, 'requests'), where('status', '==', 'open'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRequests(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOfferHelp = async (request) => {
    if (!user) {
      alert('Please log in to offer help');
      navigate('/login');
      return;
    }
    
    if (request.requesterId === user.uid) {
      alert('You cannot offer help to your own request');
      return;
    }

    setCurrentRequest(request);
    setShowOfferModal(true);
  };

  const submitOffer = async () => {
    if (!currentRequest) return;

    try {
      // DE-IDENTIFIED: Store ONLY helperId, NOT helperEmail or helperName
      await updateDoc(doc(db, 'requests', currentRequest.id), {
        status: 'pending',
        helperId: user.uid,
        // NOTE: We do NOT store helperEmail or helperName in the request document
        offerCount: increment(1)
      });

      // For notification, fetch requester info from /users (do NOT save to request)
      const requesterSnap = await getDoc(doc(db, 'users', currentRequest.requesterId));
      const requesterData = requesterSnap.data();
      
      if (requesterData) {
        await notifyHelpOffered(
          requesterData.email,
          requesterData.displayName,
          profile.displayName,
          currentRequest.title
        );
      }

      alert('Help offered successfully!');
      setShowOfferModal(false);
      fetchRequests();
    } catch (err) {
      alert('Error offering help: ' + err.message);
    }
  };

  return (
    <div className="request-feed">
      <h2> Tutoring Requests</h2>
      
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search requests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="filter-buttons">
        {subjects.map(subj => (
          <button
            key={subj}
            className={filter === subj ? 'active' : ''}
            onClick={() => setFilter(subj)}
          >
            {subj}
          </button>
        ))}
      </div>

      {loading ? (
        <p>Loading requests...</p>
      ) : filteredRequests.length === 0 ? (
        <Card>
          <p>No requests found.</p>
          <Button onClick={() => navigate('/post-request')} className="btn-primary">Post Request</Button>
        </Card>
      ) : (
        <div className="requests-list">
          {filteredRequests.map(request => (
            <Card key={request.id} className="request-card">
              <div className="request-header">
                <span className="request-subject">{request.subject}</span>
                {request.isUrgent && <span className="urgent-badge">🚨 URGENT</span>}
              </div>
              <h3>{request.title}</h3>
              <p>{request.description}</p>
              <div className="request-meta">
                <span>Posted by: {request.requesterId?.substring(0, 8)}...</span>
                <span className="separator">•</span>
                <span>{request.createdAt?.toDate?.().toLocaleDateString()}</span>
              </div>
              <Button 
                onClick={() => handleOfferHelp(request)} 
                className="btn-teal btn-full"
              >
                🤝 Offer Help
              </Button>
            </Card>
          ))}
        </div>
      )}

      {showOfferModal && (
        <div className="modal-overlay" onClick={() => setShowOfferModal(false)}>
          <Card className="rating-modal" onClick={e => e.stopPropagation()}>
            <h3>Offer Help</h3>
            <p>Request: {currentRequest?.title}</p>
            <p style={{color: '#666', fontSize: '0.9rem'}}>
              You will be matched with this student if they accept your offer.
            </p>
            <div className="btn-group">
              <Button onClick={() => setShowOfferModal(false)} className="btn-outline">Cancel</Button>
              <Button onClick={submitOffer} className="btn-primary">Confirm Offer</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
